import React, { useEffect } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import {
  MdBarChart,
  MdDashboard,
  MdHelp,
  MdHome,
  MdPerson,
  MdSettings,
} from 'react-icons/md'
import { GiPiggyBank, GiReceiveMoney } from 'react-icons/gi'
import { FaExternalLinkAlt, FaLink } from 'react-icons/fa'
import PopupSection from './PopupSection'
import { useDispatch, useSelector } from 'react-redux'
import { selectClaims, selectUserData } from 'containers/App/selectors'
import selectCustomers from 'containers/Customers/selectors'
import { useIsSystemUser } from 'utils/hooks/useHasClaim'
import { chooseProfile, logout } from 'containers/App/actions'
import CustomerCompanySelector from './CustomerCompanySelector'
import CollapsedSidebar from './CollapsedSidebar'

import styles from './styles.module.scss'
import FoldableSection from './FoldableSection'
import { useSidebarToggle } from './hooks/useSidebarToggle'
import { goToUrl, isLocal } from './functions'
import Notifications from 'containers/Notifications'
import { updateUser } from 'containers/User/actions'
import authApi from 'api/auth-api'
import { BsBoxArrowLeft } from 'react-icons/bs'
import { HasClaim } from 'utils/Authorization'
import messages from './messages'
import { injectIntl } from 'react-intl'
import { createPathPrefix } from 'utils/context'
import { ChatToggleButton } from 'containers/Chat'

function NavigationBar({ intl: { formatMessage } }) {
  const { companyCode, customerCode } = useParams()
  const user = useSelector(selectUserData)
  const customers = useSelector(selectCustomers)
  const currentCustomer = customers?.find((c) => c.code === customerCode)
  const currentCompany = currentCustomer?.companies?.find(
    (c) => c.code === companyCode
  )
  const permissions = useSelector((state) =>
    selectClaims(state, { customerCode, companyCode })
  )
  const isSystemUser = useIsSystemUser()
  const navigate = useNavigate()
  const [isVisible, setIsVisible] = useSidebarToggle()
  const dispatch = useDispatch()

  // Customer autoselect for non system users
  useEffect(() => {
    if (isSystemUser || !customers || customers.size === 0) return
    if (!customerCode && customers.size > 0) {
      const firstCustomerWithCompanies = customers.find(
        (c) => c.companies.size > 0
      )
      if (firstCustomerWithCompanies) {
        navigate(
          `/${firstCustomerWithCompanies.code}/${
            firstCustomerWithCompanies.companies.first().code
          }`
        )
        return
      }
      navigate(`/${customers.first().code}`)
    }
  }, [isSystemUser, customerCode, customers, navigate])

  // AAD/MFA logins never call the backend's login-exchange endpoint (MSAL acquires a token
  // directly from Microsoft and the SPA uses it as-is), so they miss the HangfireCookie sign-in
  // that happens as part of that flow for password/Finvoicer logins. Requesting it here instead
  // - once the nav is actually rendering the SystemSuperUser-only section - guarantees login and
  // (for multi-profile accounts) profile selection have both already completed, so the request
  // carries a resolvable identity instead of racing UserResolver's profile-disambiguation check.
  useEffect(() => {
    if (!isSystemUser) return
    authApi.signInToHangfireCookie().catch(() => {})
  }, [isSystemUser])

  if (!isVisible) {
    return <CollapsedSidebar setIsVisible={setIsVisible} />
  }

  const handleThemeChange = () => {
    const theme = user.get('theme') === 'dark' ? 'light' : 'dark'
    dispatch(
      updateUser(user.toJS(), {
        ...user.toJS(),
        theme,
      })
    )
  }

  const Link = ({ claim, ...props }) => {
    return !claim || HasClaim({ permissions, claim }) ? (
      <RouterLink {...props} />
    ) : null
  }

  return (
    <nav className={styles.sidebar} role="navigation" aria-label="Navigation">
      <div className={styles.logoWrapper}>
        <CustomerCompanySelector
          currentCustomer={currentCustomer}
          currentCompany={currentCompany}
        />
      </div>

      <div className={styles.seperator} />

      <div className={styles.scrollableNavElements}>
        {companyCode && (
          <>
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <Link
                to={
                  user.get('defaultPage') ||
                  `${customerCode}/${companyCode}/dashboards`
                }
              >
                <MdHome size={22} />
                <span>{formatMessage(messages.home)}</span>
              </Link>
              <button
                onClick={(e) => {
                  e.preventDefault()
                  setIsVisible(false)
                }}
                style={{ width: '48px', height: '100%' }}
              >
                <BsBoxArrowLeft size={20} style={{ marginRight: '2px' }} />
              </button>
            </span>
            <Link to={`${customerCode}/${companyCode}/dashboards`}>
              <MdDashboard size={22} />
              <span>{formatMessage(messages.dashboards)}</span>
            </Link>
            <Link to={`${customerCode}/${companyCode}/budgets`}>
              <GiPiggyBank size={22} />
              <span>{formatMessage(messages.budgets)}</span>
            </Link>
            <Link to={`${customerCode}/${companyCode}/reports`}>
              <MdBarChart size={22} />
              <span>{formatMessage(messages.reports)}</span>
            </Link>
            <Link
              to={`${customerCode}/${companyCode}/cashflow`}
              claim="CashFlowRead"
            >
              <GiReceiveMoney size={22} />
              <span>{formatMessage(messages.cashFlow)}</span>
            </Link>
            <div className={styles.seperator} />
            {currentCompany?.links?.length > 0 && (
              <>
                <FoldableSection
                  title={
                    <span>
                      <FaLink size={20} style={{ marginRight: '7px' }} />
                      {currentCompany.linksNavigationTitle ||
                        formatMessage(messages.tools)}
                    </span>
                  }
                  show={true}
                >
                  {currentCompany.links.map((link) =>
                    isLocal({ url: link.url }) ? (
                      <button
                        onClick={() => goToUrl({ url: link.url, navigate })}
                        key={link.name}
                      >
                        <span>{link.name}</span>
                      </button>
                    ) : (
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        key={link.name}
                      >
                        <span>{link.name}</span>
                        <FaExternalLinkAlt
                          size={11}
                          style={{ marginLeft: '5px' }}
                        />
                      </a>
                    )
                  )}
                </FoldableSection>
              </>
            )}
          </>
        )}
        {!companyCode && customerCode && (
          <>
            <Link to={`${customerCode}/accountschemetemplates`}>
              <span>{formatMessage(messages.accountSchemeTemplates)}</span>
            </Link>
            <Link to={`${customerCode}/calculationconstants`}>
              <span>{formatMessage(messages.calculationConstants)}</span>
            </Link>
            <Link to={`${customerCode}`}>
              <span>{formatMessage(messages.companies)}</span>
            </Link>
            <Link to={`${customerCode}/customerdashboards`}>
              <span>{formatMessage(messages.dashboards)}</span>
            </Link>
            <Link to={`${customerCode}/reportschemes`}>
              <span>{formatMessage(messages.reportSchemes)}</span>
            </Link>
            <Link to={`${customerCode}/reportschemetemplates`}>
              <span>{formatMessage(messages.reportSchemeTemplates)}</span>
            </Link>
            <Link to={`${customerCode}/customerreports`}>
              <span>{formatMessage(messages.reports)}</span>
            </Link>
            <Link to={`customers/${customerCode}`}>
              <span>{formatMessage(messages.settings)}</span>
            </Link>
            <Link to={`${customerCode}/users`}>
              <span>{formatMessage(messages.users)}</span>
            </Link>
          </>
        )}
        {!companyCode && !customerCode && isSystemUser && (
          <>
            <Link to="/accountschemetemplates">
              <span>{formatMessage(messages.accountSchemeTemplates)}</span>
            </Link>
            <Link to="/allusers">
              <span>{formatMessage(messages.allUsers)}</span>
            </Link>
            <Link to="/calculationconstants">
              <span>{formatMessage(messages.calculationConstants)}</span>
            </Link>
            <Link to="/chatdocumentationindex">
              <span>{formatMessage(messages.chatDocumentationIndex)}</span>
            </Link>
            <Link to="/customers">
              <span>{formatMessage(messages.customers)}</span>
            </Link>
            <Link to="/systemdashboards">
              <span>{formatMessage(messages.dashboards)}</span>
            </Link>
            <a
              href={`${process.env.REACT_APP_GT_API}hangfire/`}
              target="_blank"
              rel="noreferrer"
            >
              <span>{formatMessage(messages.gtScheduledJobs)}</span>
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_API}hangfire`}
              target="_blank"
              rel="noreferrer"
            >
              <span>{formatMessage(messages.hangfireJobs)}</span>
            </a>
            <Link to="/integrationsourcesystems">
              <span>{formatMessage(messages.integrationSourceSystems)}</span>
            </Link>
            <Link to="/reportschemes">
              <span>{formatMessage(messages.reportSchemes)}</span>
            </Link>
            <Link to="/reportschemetemplates">
              <span>{formatMessage(messages.reportSchemeTemplates)}</span>
            </Link>
            <Link to="/systemreportselections-beta">
              <span>{formatMessage(messages.reports)}</span>
            </Link>
            <Link to="/users">
              <span>{formatMessage(messages.users)}</span>
            </Link>
          </>
        )}

        <FoldableSection
          title={
            <>
              <MdSettings size={22} />
              <span style={{ marginLeft: '5px' }} id="settings-dropdown">
                {formatMessage(messages.settings)}
              </span>
            </>
          }
          show={!!companyCode}
        >
          <Link to={`${customerCode}/${companyCode}/accounts`} claim="Accounts">
            <span>{formatMessage(messages.accounts)}</span>
          </Link>
          <Link
            to={`${customerCode}/${companyCode}/accountingperiods`}
            claim="AccountingPeriodsRead"
          >
            <span>{formatMessage(messages.accountingPeriods)}</span>
          </Link>
          <Link
            to={`${customerCode}/${companyCode}/accountschemes`}
            claim="AccountSchemesRead"
          >
            <span>{formatMessage(messages.accountSchemes)}</span>
          </Link>
          <Link
            to={`${customerCode}/${companyCode}/accountschemetemplates`}
            claim="AccountSchemeTemplates"
          >
            <span>{formatMessage(messages.accountSchemeTemplates)}</span>
          </Link>
          <Link
            to={`${customerCode}/${companyCode}/calculationconstants`}
            claim="CalculationConstants"
          >
            <span>{formatMessage(messages.calculationConstants)}</span>
          </Link>
          <Link
            to={`${customerCode}/${companyCode}/dimensions`}
            claim="DimensionRead"
          >
            <span>{formatMessage(messages.dimensions)}</span>
          </Link>
          <Link
            to={`${customerCode}/${companyCode}/integrationlogs`}
            claim="CompanyEdit"
          >
            <span>{formatMessage(messages.integrationLogs)}</span>
          </Link>
          <Link
            to={`${customerCode}/${companyCode}/reportinggroups`}
            claim="ReportingGroupRead"
          >
            <span>{formatMessage(messages.reportingGroups)}</span>
          </Link>
          <Link
            to={`${customerCode}/${companyCode}/reportschemes`}
            claim="ReportsSchemesRead"
          >
            <span>{formatMessage(messages.reportSchemes)}</span>
          </Link>
          <Link
            to={`${customerCode}/${companyCode}/rollingrules`}
            claim="RollingRuleTemplatesRead"
          >
            <span>{formatMessage(messages.rollingRuleTemplates)}</span>
          </Link>
          <Link
            to={`${customerCode}/${companyCode}/vouchers`}
            claim="VoucherRead"
          >
            <span>{formatMessage(messages.vouchers)}</span>
          </Link>
          <div className={styles.seperator} />
          <Link to={`${customerCode}/${companyCode}/roles`} claim="RoleRead">
            <span>{formatMessage(messages.roles)}</span>
          </Link>
          <Link to={`${customerCode}/${companyCode}/users`} claim="UserRead">
            <span>{formatMessage(messages.users)}</span>
          </Link>
          <Link
            to={`${customerCode}/companies/${companyCode}`}
            claim="CompanyEdit"
          >
            <span>{formatMessage(messages.companySettings)}</span>
          </Link>
        </FoldableSection>
        <div className={styles.flexGrow} />

        <a
          href="https://lemondoc.atlassian.net/wiki/spaces/Finazilla"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.withIcon}
        >
          <MdHelp size={22} />
          <span>{formatMessage(messages.helpCenter)}</span>
          <FaExternalLinkAlt size={11} style={{ marginLeft: '5px' }} />
        </a>
        <Notifications
          user={user}
          customerCode={customerCode}
          companyCode={companyCode}
        />
        <ChatToggleButton />
      </div>
      <div className={styles.seperator} />
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <PopupSection
          title={
            <>
              <MdPerson size={22} />
              <div
                className={styles.withSubtitle}
                style={{ maxWidth: '130px', overflow: 'hidden' }}
              >
                <div className={styles.subTitle}>
                  {formatMessage(messages.signedInAs)}
                </div>
                <div aria-label="Current user">{user.get('name')}</div>
              </div>
            </>
          }
        >
          <button className={'noClose'} onClick={handleThemeChange}>
            {user.get('theme') === 'dark'
              ? formatMessage(messages.useLightTheme)
              : formatMessage(messages.useDarkTheme)}
          </button>
          <Link
            to={
              createPathPrefix(customerCode, companyCode) +
              `users/${user.get('id')}?tab=settings`
            }
          >
            <span>{formatMessage(messages.settings)}</span>
          </Link>
          <button
            onClick={() => {
              dispatch(chooseProfile(user, navigate))
            }}
          >
            <span>{formatMessage(messages.switchProfile)}</span>
          </button>
          <button
            onClick={() => {
              dispatch(logout())
            }}
          >
            <span>{formatMessage(messages.logout)}</span>
          </button>
        </PopupSection>
        <button
          onClick={(e) => {
            e.preventDefault()
            setIsVisible(false)
          }}
          style={{ width: '48px', height: '100%' }}
        >
          <BsBoxArrowLeft size={20} style={{ marginRight: '2px' }} />
        </button>
      </span>
    </nav>
  )
}

export default injectIntl(NavigationBar)
